import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarIcon, FileText, Save, X } from "lucide-react";
import { format } from "date-fns";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { contractorService } from "@/services/contractorService";
import {
  contractorFormSchema, 
  ContractorAgreementFormData,
  GenerateContractorAgreementResponse,
  CreateContractorContractResponse,
  ContractorContract
} from "@/types/contractor-agreement";
import DashboardContainer from "@/components/layout/DashboardContainer";
import { useCountry } from "@/contexts/CountryContext";

interface ContractorAgreementDashboardFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  isEditMode?: boolean;
  initialContract?: ContractorContract;
}

export default function ContractorAgreementDashboardForm({ 
  onSuccess, 
  onCancel,
  isEditMode = false,
  initialContract
}: ContractorAgreementDashboardFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getCurrencySymbol, currentCountry, getDateFormat, formatDate } = useCountry();

  // Generate currency options with current country's currency first
  const getCurrencyOptions = () => {
    const allOptions = [
      { value: "£", label: "£ (GBP)" },
      { value: "$", label: "$ (USD)" },
      { value: "€", label: "€ (EUR)" },
    ];
    
    const currentCurrency = getCurrencySymbol();
    const currentOption = allOptions.find(option => option.value === currentCurrency);
    const otherOptions = allOptions.filter(option => option.value !== currentCurrency);
    
    return currentOption ? [currentOption, ...otherOptions] : allOptions;
  };

  // Get country-specific placeholder text
  const getPlaceholders = () => {
    const isUS = currentCountry?.code === 'US';
    
    return {
      clientName: isUS ? "e.g., Jane Smith or ABC Inc." : "e.g., Jane Smith or ABC Ltd",
      clientAddress: isUS ? "e.g., 123 Main St, New York, NY 10001" : "e.g., 123 Client St, London",
      contractorAddress: isUS ? "e.g., 456 Oak Ave, Los Angeles, CA 90001" : "e.g., 456 Contractor Ave, London"
    };
  };

  // Get country-specific background check text
  const getBackgroundCheckDefaults = () => {
    const isUS = currentCountry?.code === 'US';
    
    return {
      defaultExample: isUS ? "Criminal background check" : "DBS certificate",
      placeholder: isUS ? "e.g., Criminal background check, references" : "e.g., DBS certificate, references"
    };
  };

  // Get country-specific date formatting
  const getDateDefaults = () => {
    const isUS = currentCountry?.code === 'US';
    
    return {
      displayFormat: isUS ? "MM/dd/yyyy" : "dd/MM/yyyy",
      exampleDate: isUS ? "12/31/2023" : "31/12/2023",
      endDatePlaceholder: isUS ? "e.g., 12/31/2023 or On project completion" : "e.g., 31/12/2023 or On project completion"
    };
  };

  // Get country-specific data protection regulation options
  const getDataProtectionDefaults = () => {
    const isUS = currentCountry?.code === 'US';
    
    return {
      defaultRegulation: isUS ? "State Privacy Laws" : "UK GDPR",
      options: isUS ? [
        { value: "State Privacy Laws", label: "State Privacy Laws" },
        { value: "CCPA", label: "CCPA (California)" },
        { value: "No Federal Regulation", label: "No Federal Regulation" },
        { value: "Other", label: "Other" }
      ] : [
        { value: "UK GDPR", label: "UK GDPR" },
        { value: "EU GDPR", label: "EU GDPR" },
        { value: "CCPA", label: "CCPA (California)" },
        { value: "PIPEDA", label: "PIPEDA (Canada)" },
        { value: "Other", label: "Other" }
      ]
    };
  };

  // Get country-specific governing law options
  const getGoverningLawDefaults = () => {
    const isUS = currentCountry?.code === 'US';
    
    return {
      defaultLaw: isUS ? "United States" : "United Kingdom",
      options: isUS ? [
        { value: "United States", label: "United States" },
        { value: "United Kingdom", label: "United Kingdom" },
        { value: "Canada", label: "Canada" },
        { value: "Other", label: "Other" }
      ] : [
        { value: "United Kingdom", label: "United Kingdom" },
        { value: "England and Wales", label: "England and Wales" },
        { value: "Scotland", label: "Scotland" },
        { value: "Northern Ireland", label: "Northern Ireland" },
        { value: "United States", label: "United States" },
        { value: "Other", label: "Other" }
      ]
    };
  };

  // Get country-specific mediation rules options
  const getMediationRulesDefaults = () => {
    const isUS = currentCountry?.code === 'US';
    
    return {
      defaultRules: isUS ? "AAA" : "CEDR",
      options: isUS ? [
        { value: "AAA", label: "AAA (American Arbitration Association)" },
        { value: "ICC", label: "ICC (International Chamber of Commerce)" },
        { value: "Other", label: "Other" }
      ] : [
        { value: "CEDR", label: "CEDR (Centre for Effective Dispute Resolution)" },
        { value: "AAA", label: "AAA (American Arbitration Association)" },
        { value: "ICC", label: "ICC (International Chamber of Commerce)" },
        { value: "Other", label: "Other" }
      ]
    };
  };

  // Create a modified schema that excludes user contact fields for internal use
  const internalFormSchema = contractorFormSchema.omit({
    userFirstName: true,
    userLastName: true,
    userEmail: true,
  });

  // Function to get initial form values from existing contract
  const getInitialFormValues = () => {
    if (isEditMode && initialContract) {
      const terms = initialContract.contractTerms;
      return {
        // Parties
        client_full_legal_name: terms.client_full_legal_name || "",
        client_address: terms.client_address || "",
        contractor_full_legal_name: terms.contractor_full_legal_name || "",
        contractor_address: terms.contractor_address || "",
        contractor_business_name: terms.contractor_business_name || "",

        // Services
        schedule_a_services_description: terms.schedule_a_services_description || "",

        // Fees & Invoicing
        fee_currency_symbol: terms.fee_currency_symbol || getCurrencySymbol(),
        fee_rate: terms.fee_rate ? parseFloat(terms.fee_rate.toString()) : undefined as unknown as number,
        fee_basis: terms.fee_basis || "hour",
        invoice_frequency: terms.invoice_frequency || "monthly",
        payment_due_days: terms.payment_due_days || 30,
        schedule_b_fees_description: terms.schedule_b_fees_description || "",

        // Safeguarding & Background Checks
        background_check_examples: terms.background_check_examples || getBackgroundCheckDefaults().defaultExample,
        schedule_c_safeguarding_description: terms.schedule_c_safeguarding_description || "",

        // Confidentiality & Data Protection
        data_protection_regulation: terms.data_protection_regulation || getDataProtectionDefaults().defaultRegulation,

        // Insurance & Liability
        has_insurance: terms.has_insurance || false,
        insurance_currency_symbol: terms.insurance_currency_symbol || getCurrencySymbol(),
        insurance_cover_amount: terms.insurance_cover_amount ? parseFloat(terms.insurance_cover_amount.toString()) : undefined as unknown as number,
        liability_cap_currency_symbol: terms.liability_cap_currency_symbol || getCurrencySymbol(),
        liability_cap_amount: terms.liability_cap_amount ? parseFloat(terms.liability_cap_amount.toString()) : undefined as unknown as number,

        // Term & Termination
        start_date: terms.start_date || "",
        end_date_or_completion: terms.end_date_or_completion || "",
        termination_notice_days: terms.termination_notice_days || 30,

        // Non-Solicitation (Optional)
        non_solicitation_clause: terms.non_solicitation_clause || false,
        non_solicitation_months: terms.non_solicitation_months || 6,

        // Governing Law & Dispute Resolution
        governing_law_country: terms.governing_law_country || getGoverningLawDefaults().defaultLaw,
        mediation_rules: terms.mediation_rules || getMediationRulesDefaults().defaultRules,
      };
    }

    // Default values for create mode
    return {
      // Parties
      client_full_legal_name: "",
      client_address: "",
      contractor_full_legal_name: "",
      contractor_address: "",
      contractor_business_name: "",

      // Services
      schedule_a_services_description: "",

      // Fees & Invoicing
      fee_currency_symbol: getCurrencySymbol(),
      fee_rate: undefined as unknown as number,
      fee_basis: "hour",
      invoice_frequency: "monthly",
      payment_due_days: 30,
      schedule_b_fees_description: "",

      // Safeguarding & Background Checks
      background_check_examples: getBackgroundCheckDefaults().defaultExample,
      schedule_c_safeguarding_description: "",

      // Confidentiality & Data Protection
      data_protection_regulation: getDataProtectionDefaults().defaultRegulation,

      // Insurance & Liability
      has_insurance: false,
      insurance_currency_symbol: getCurrencySymbol(),
      insurance_cover_amount: undefined as unknown as number,
      liability_cap_currency_symbol: getCurrencySymbol(),
      liability_cap_amount: undefined as unknown as number,

      // Term & Termination
      start_date: "",
      end_date_or_completion: "",
      termination_notice_days: 30,

      // Non-Solicitation (Optional)
      non_solicitation_clause: false,
      non_solicitation_months: 6,

      // Governing Law & Dispute Resolution
      governing_law_country: getGoverningLawDefaults().defaultLaw,
      mediation_rules: getMediationRulesDefaults().defaultRules,
    };
  };

  const form = useForm<Omit<ContractorAgreementFormData, 'userFirstName' | 'userLastName' | 'userEmail'>>({
    resolver: zodResolver(internalFormSchema),
    defaultValues: getInitialFormValues(),
  });

  async function onSubmit(data: Omit<ContractorAgreementFormData, 'userFirstName' | 'userLastName' | 'userEmail'>) {
    try {
      setIsSubmitting(true);

      let response: CreateContractorContractResponse;

      if (isEditMode && initialContract) {
        // Edit mode: call updateContract service
        response = await contractorService.updateContract(
          initialContract.id,
          data as ContractorAgreementFormData,
          false // Don't regenerate PDF by default
        );
      } else {
        // Create mode: call the original API
        response = await ApiService.request<CreateContractorContractResponse>(
          EndpointKeys.CREATE_CONTRACTOR_CONTRACT,
          {
            method: "POST",
            body: { 
              agreementData: data,
            },
            skipAuth: false,
          },
        );
      }

      if (response.status === "success" && response.contract) {
        toast({
          title: "Success!",
          description: isEditMode 
            ? "Your contractor contract has been updated successfully." 
            : "Your contractor contract has been created successfully.",
        });
        
        if (!isEditMode) {
          form.reset();
        }
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(response.error || `Failed to ${isEditMode ? 'update' : 'create'} contractor contract`);
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to create contractor contract",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <DashboardContainer 
      title={isEditMode ? "Edit Contractor Agreement" : "Create Contractor Agreement"}
      headerContent={
        onCancel && (
          <Button variant="outline" onClick={onCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        )
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Parties Information */}
          <Card>
            <CardHeader>
              <CardTitle>Parties Information</CardTitle>
              <CardDescription>
                Enter details about the client and contractor.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="client_full_legal_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client Full Legal Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={getPlaceholders().clientName}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="client_address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client Address</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={getPlaceholders().clientAddress}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contractor_full_legal_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contractor Full Legal Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., John Doe"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contractor_address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contractor Address</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={getPlaceholders().contractorAddress}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="contractor_business_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contractor Business Name (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g., John Doe Services"
                      />
                    </FormControl>
                    <FormDescription>
                      If the contractor operates under a business name, enter it here.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Services */}
          <Card>
            <CardHeader>
              <CardTitle>Services Description</CardTitle>
              <CardDescription>
                Define the services to be provided by the contractor.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="schedule_a_services_description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description of Services</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Describe in detail the services the contractor will provide..."
                        className="min-h-[100px]"
                      />
                    </FormControl>
                    <FormDescription>
                      Be specific about the contractor's responsibilities, deliverables, and expected outcomes.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Fees & Invoicing */}
          <Card>
            <CardHeader>
              <CardTitle>Fees & Invoicing</CardTitle>
              <CardDescription>
                Specify payment details and invoicing arrangements.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="grid grid-cols-3 gap-2 items-end">
                  <FormField
                    control={form.control}
                    name="fee_currency_symbol"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Currency</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Currency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {getCurrencyOptions().map(option => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="fee_rate"
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>Fee Rate</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                            placeholder="e.g., 50"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="fee_basis"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fee Basis</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select fee basis" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="hour">Per Hour</SelectItem>
                          <SelectItem value="day">Per Day</SelectItem>
                          <SelectItem value="week">Per Week</SelectItem>
                          <SelectItem value="month">Per Month</SelectItem>
                          <SelectItem value="project">Per Project</SelectItem>
                          <SelectItem value="milestone">Per Milestone</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="invoice_frequency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Invoice Frequency</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select invoice frequency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="fortnightly">Fortnightly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="milestone">After Each Milestone</SelectItem>
                          <SelectItem value="completion">Upon Completion</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="payment_due_days"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Due (Days)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                          placeholder="e.g., 30"
                        />
                      </FormControl>
                      <FormDescription>
                        Number of days after invoice receipt when payment is due.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="schedule_b_fees_description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Additional Fee Details (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Describe any additional fee arrangements, expenses, or payment terms..."
                        className="min-h-[80px]"
                      />
                    </FormControl>
                    <FormDescription>
                      Include details on expenses reimbursement, late payment fees, etc.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Safeguarding & Data Protection */}
          <Card>
            <CardHeader>
              <CardTitle>Safeguarding & Data Protection</CardTitle>
              <CardDescription>
                Specify background check requirements and data protection regulations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="background_check_examples"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Required Background Checks</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={getBackgroundCheckDefaults().placeholder}
                      />
                    </FormControl>
                    <FormDescription>
                      List the background checks or clearances required.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="schedule_c_safeguarding_description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Additional Safeguarding Requirements (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Describe any additional safeguarding policies or requirements..."
                        className="min-h-[80px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="data_protection_regulation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data Protection Regulation</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select data protection regulation" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getDataProtectionDefaults().options.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Insurance & Liability */}
          <Card>
            <CardHeader>
              <CardTitle>Insurance & Liability</CardTitle>
              <CardDescription>
                Specify insurance requirements and liability limitations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="has_insurance"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        Contractor Must Have Insurance
                      </FormLabel>
                      <FormDescription>
                        Check this if the contractor must maintain professional insurance.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {form.watch("has_insurance") && (
                <div className="border rounded-md p-4">
                  <FormLabel className="mb-3 block">Insurance Cover Amount</FormLabel>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <FormField
                      control={form.control}
                      name="insurance_currency_symbol"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Currency</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Currency" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {getCurrencyOptions().map(option => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="insurance_cover_amount"
                      render={({ field }) => (
                        <FormItem className="md:col-span-3">
                          <FormLabel>Amount</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              placeholder="e.g., 1000000"
                            />
                          </FormControl>
                          <FormDescription>
                            Minimum coverage required.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}

              <div className="border rounded-md p-4">
                <FormLabel className="mb-3 block">Liability Cap Amount</FormLabel>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <FormField
                    control={form.control}
                    name="liability_cap_currency_symbol"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Currency</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Currency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {getCurrencyOptions().map(option => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="liability_cap_amount"
                    render={({ field }) => (
                      <FormItem className="md:col-span-3">
                        <FormLabel>Amount</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                            placeholder="e.g., 10000"
                          />
                        </FormControl>
                        <FormDescription>
                          Maximum liability amount (optional).
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Term & Termination */}
          <Card>
            <CardHeader>
              <CardTitle>Term & Termination</CardTitle>
              <CardDescription>
                Specify the agreement duration and termination terms.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="start_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Start Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? (
                                format(
                                  new Date(field.value + "T00:00:00"),
                                  getDateDefaults().displayFormat,
                                )
                              ) : (
                                <span>Pick a date</span>
                              )}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 z-[100]">
                          <Calendar
                            mode="single"
                            selected={
                              field.value
                                ? new Date(field.value + "T00:00:00")
                                : undefined
                            }
                            onSelect={(date) =>
                              field.onChange(
                                date ? format(date, "yyyy-MM-dd") : "",
                              )
                            }
                            classNames={{
                              nav_button: "h-7 w-7 bg-transparent p-0 text-gray-700 hover:text-gray-900",
                              caption_label: "text-sm font-medium text-gray-900",
                              day_outside: "day-outside text-gray-600 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
                              day_disabled: "text-gray-400 opacity-50 cursor-not-allowed",
                              day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-gray-900 hover:bg-accent hover:text-accent-foreground"
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="end_date_or_completion"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End Date or Completion Criteria</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={getDateDefaults().endDatePlaceholder}
                        />
                      </FormControl>
                      <FormDescription>
                        Enter a specific end date or describe completion criteria.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="termination_notice_days"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Termination Notice Period (Days)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                          placeholder="e.g., 30"
                        />
                      </FormControl>
                      <FormDescription>
                        Number of days' notice required to terminate the agreement.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Non-Solicitation */}
          <Card>
            <CardHeader>
              <CardTitle>Non-Solicitation</CardTitle>
              <CardDescription>
                Optional clause to prevent contractor from soliciting client's employees or customers.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="non_solicitation_clause"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        Include Non-Solicitation Clause
                      </FormLabel>
                      <FormDescription>
                        Prevents the contractor from soliciting your employees, clients, or customers.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {form.watch("non_solicitation_clause") && (
                <FormField
                  control={form.control}
                  name="non_solicitation_months"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Non-Solicitation Period (Months)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                          placeholder="e.g., 6"
                        />
                      </FormControl>
                      <FormDescription>
                        Number of months the restriction applies after contract ends.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </CardContent>
          </Card>

          {/* Governing Law & Dispute Resolution */}
          <Card>
            <CardHeader>
              <CardTitle>Governing Law & Dispute Resolution</CardTitle>
              <CardDescription>
                Specify the governing law and dispute resolution procedures.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="governing_law_country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Governing Law Country</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {getGoverningLawDefaults().options.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="mediation_rules"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mediation Rules</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select mediation rules" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {getMediationRulesDefaults().options.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => form.reset()}
              disabled={isSubmitting}
            >
              Clear Form
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Save className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Generate Agreement
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </DashboardContainer>
  );
} 